// React error boundary.
//
// Two render modes:
//   • Whole-app (default): full-screen "Something went wrong" + Reload
//     button that navigates to "/". Used in main.tsx wrapping <App />.
//   • Per-route (props.resettable): compact glass-card fallback that
//     stays inside the page's layout, with a "Try again" button that
//     resets the boundary's internal state. Used on PostComposer,
//     MyVideos, AddAcademyVideoModal — surfaces where a crash in one
//     section shouldn't white-screen the whole app.
//
// Both modes capture the error to Sentry via reportError() so failures
// are visible regardless of the rendered fallback. Existing call sites
// without the new props see the unchanged whole-app behaviour.

import { Component, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";
import { reportError } from "@/lib/sentry";

interface Props {
  children: ReactNode;
  /** Custom message for the compact fallback. Defaults to a generic
   *  "Something went wrong" if not provided. */
  fallbackMessage?: string;
  /** When true: render compact in-place fallback with a Try-again
   *  button that resets state. When false/omitted: full-screen
   *  fallback with a Reload button (whole-app behaviour). */
  resettable?: boolean;
  /** Optional Sentry tag so per-route boundaries are distinguishable in
   *  the dashboard. Falls back to "global". */
  boundaryName?: string;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: unknown) {
    // eslint-disable-next-line no-console
    console.error("[ErrorBoundary]", error, info);
    reportError(error, {
      boundary: this.props.boundaryName ?? "global",
      // info.componentStack — useful but can be large; capture as a
      // string so Sentry's extras stay readable.
      componentStack:
        typeof info === "object" && info !== null && "componentStack" in info
          ? String((info as { componentStack: unknown }).componentStack)
          : undefined,
    });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    // Per-route compact fallback — stays in flow so the surrounding
    // page chrome (nav, tabs, banners) keeps working.
    if (this.props.resettable) {
      return (
        <div className="glass-card rounded-2xl p-6 max-w-md mx-auto text-center my-6">
          <div className="w-12 h-12 rounded-2xl bg-destructive/15 border border-destructive/30 mx-auto flex items-center justify-center mb-3">
            <AlertTriangle className="w-6 h-6 text-destructive" />
          </div>
          <p className="text-sm font-medium text-foreground mb-1">
            {this.props.fallbackMessage ?? "Something went wrong."}
          </p>
          <p className="text-xs text-muted-foreground mb-4">
            Please try again. If it keeps happening, contact support.
          </p>
          <button
            type="button"
            onClick={this.handleReset}
            className="px-4 py-2 rounded-lg bg-secondary text-sm font-medium hover:bg-secondary/80 transition-colors"
          >
            Try again
          </button>
          {import.meta.env.DEV && this.state.error && (
            <pre className="text-left text-[10px] text-muted-foreground mt-4 overflow-auto max-h-32 bg-muted/20 p-3 rounded-lg">
              {this.state.error.message}
            </pre>
          )}
        </div>
      );
    }

    // Whole-app fallback (default).
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="glass-card rounded-2xl p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 rounded-2xl bg-destructive/15 border border-destructive/30 mx-auto flex items-center justify-center mb-4">
            <AlertTriangle className="w-8 h-8 text-destructive" />
          </div>
          <h1 className="font-heading text-xl font-bold mb-2">Something went wrong</h1>
          <p className="text-sm text-muted-foreground mb-5">
            We hit an unexpected error. Please reload the page — if it keeps happening, contact support.
          </p>
          <button
            onClick={() => window.location.assign("/")}
            className="px-5 py-2.5 rounded-xl gradient-green text-background font-semibold text-sm"
          >
            Reload
          </button>
          {import.meta.env.DEV && this.state.error && (
            <pre className="text-left text-[10px] text-muted-foreground mt-5 overflow-auto max-h-40 bg-muted/20 p-3 rounded-lg">
              {this.state.error.message}
            </pre>
          )}
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
